/**
 * Parses a finite number. Returns null for null, empty, NaN, or non-finite values.
 */
export function parseFiniteNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

/** Coerces unknown to string for select fields; empty string when unset. */
export function coerceSelectString(value: unknown): string {
  return value == null || value === '' ? '' : String(value);
}

/** Coerces unknown to number or empty string for optional numeric form fields. */
export function coerceFormNumberValue(value: unknown): number | '' {
  if (value == null || value === '') return '';
  const num = Number(value);
  return Number.isNaN(num) ? '' : num;
}

/** Coerces API date/datetime to YYYY-MM-DD form value. */
export function coerceFormDateValue(value: unknown): string {
  if (value == null || value === '') return '';
  const text = String(value);
  if (text.includes('T')) return text.split('T')[0] ?? '';
  return text;
}

/** True when value is a meaningful form input (non-empty, non-false). */
export function isMeaningfulFormValue(value: unknown): boolean {
  return value != null && value !== '' && value !== false;
}

/**
 * Validates JSON string is a non-null object (not a bare number).
 * Returns null when valid; error message when invalid. Empty string is valid (required rule handles empties).
 */
export function validateJsonObjectString(value: unknown): string | null {
  if (value == null || String(value).trim() === '') return null;
  try {
    const parsed = JSON.parse(String(value));
    if (typeof parsed === 'number') return 'Invalid JSON format';
    if (typeof parsed !== 'object' || parsed === null) return 'Invalid JSON format';
    return null;
  } catch {
    return 'Invalid JSON format';
  }
}

/**
 * Parses a value to a numeric ID. Returns undefined for null, empty, or NaN values.
 */
export function parseNumericId(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? undefined : numeric;
}

/**
 * Strict numeric ID parser — rejects non-digit strings (e.g. "abc", "1.5").
 * Only accepts whole-number digit strings.
 */
export function parseStrictNumericId(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  if (typeof value === 'string' && !/^\d+$/.test(value.trim())) return undefined;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? undefined : numeric;
}

/**
 * Strips commas from a value. Returns null for null/empty.
 */
export function stripCommas(value: unknown): string | null {
  if (value == null || value === '') return null;
  return String(value).replaceAll(',', '');
}

/**
 * Capitalizes the first letter of a value. Handles unknown input types.
 */
export function capitalizeName(value: unknown): string {
  if (value == null || value === '') return '';
  return String(value).charAt(0).toUpperCase() + String(value).slice(1);
}

/**
 * Normalizes raw form data into an array of record objects.
 * Handles: arrays, objects with `.items`, single objects, and nulls.
 */
export function normalizeSubmitRows(raw: unknown): Record<string, unknown>[] {
  if (Array.isArray(raw)) {
    return raw.filter(
      (item): item is Record<string, unknown> => item != null && typeof item === 'object',
    );
  }
  if (raw != null && typeof raw === 'object' && Array.isArray((raw as Record<string, unknown>).items)) {
    return normalizeSubmitRows((raw as Record<string, unknown>).items);
  }
  return raw != null && typeof raw === 'object' ? [raw as Record<string, unknown>] : [];
}

/**
 * Converts a 0-based index to a 1-based legacy S.N. string.
 */
export function legacyRowSn(index: number): string {
  return String(index + 1);
}
